import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import { useRowDeletePing } from '../../hooks/realtime/useRowDeletePing';
import { fetchNoticeReceipts, upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isAssuranceSettled } from '../../utils/auth/assuranceSettled';
import { withTimeout } from '../../utils/async/withTimeout';
import {
  setNoticeReceiptsSession,
  clearNoticeReceiptsSession,
} from '../../utils/notices/noticeReceiptsSession';
import { setNoticeReceiptsReady } from '../../utils/notices/noticeReceiptsReady';
import { emitNoticeReceiptsChanged } from '../../utils/notices/noticeReceiptsBus';
import { maxIso } from '../../utils/notices/noticeReceiptMath';
import {
  setLastFetchedNoticeReceipts,
  getLastFetchedNoticeReceipts,
  clearLastFetchedNoticeReceipts,
} from '../../utils/notices/noticeReceiptsCache';
import {
  applyNoticeReceiptsToLocalStores,
  collectAllLocalNoticeReceipts,
} from '../../utils/notices/noticeReceiptAdapters';

/** Max wait for one receipts read (RD-SESS-034). */
const HYDRATE_TIMEOUT_MS = 8000;

/** Backup for a dropped socket — a resume re-fetch this often at most. */
const RESUME_MIN_INTERVAL_MS = 30000;

const RECEIPT_STAMP_FIELDS = ['announced_at', 'acked_at', 'snoozed_until'];

/** Field-wise MAX, never a row replace (audit 2026-09-26, class B): a stale
 *  realtime row or re-fetch carrying `acked_at: null` must not wipe an ack
 *  the cache already holds — a late-mounting consumer (AI Hub) reads this. */
function mergeFetchedIntoCache(nextRows) {
  const byKey = new Map(getLastFetchedNoticeReceipts().map((r) => [r.notice_key, r]));
  (nextRows || []).forEach((r) => {
    if (!r?.notice_key) return;
    const prev = byKey.get(r.notice_key);
    if (!prev) { byKey.set(r.notice_key, r); return; }
    const merged = { ...prev, ...r };
    RECEIPT_STAMP_FIELDS.forEach((field) => { merged[field] = maxIso(prev[field], r[field]); });
    byKey.set(r.notice_key, merged);
  });
  setLastFetchedNoticeReceipts(Array.from(byKey.values()));
}

function receiptFieldsDiffer(local, server) {
  if (!server) return true;
  return RECEIPT_STAMP_FIELDS
    .some((field) => (local[field] || null) !== (server[field] || null));
}

/**
 * RD-NOTICES-V2 — the ONE mount point that turns per-BROWSER notice receipts
 * (estimate-response seen stamps, Office Inbox seen, worker route-change
 * acks, the MFA nudge snooze, the owner-transfer notice) into per-USER,
 * server-synced, live-updated-across-sessions receipts. Renders nothing.
 *
 * Mounted once by `SessionScopedShell`, keyed by `${sessionUser.id}:${tenantId}`
 * so a tenant switch (or sign-out/sign-in) gets a fresh hydration rather than
 * silently reusing the previous session's in-flight state.
 *
 * Flow: wait for the session's FINAL assurance level (aal1 → aal2 after the
 * 2FA challenge re-runs this on the auth event) → hydrate (fetch → merge
 * server rows into every local store → upload this device's local-only/
 * newer-local receipts once) → mark READY ONLY after a successful fetch.
 * DECISION (RD-SESS-034): the old 4s "greet anyway" fallback is GONE. Every
 * consumer of READY is a one-time greeting/auto-open, and re-greeting
 * (what the fallback did on a slow/denied read) is the bug; a failed or
 * never-completing hydration now keeps greetings SILENT, retried on resume
 * (visibilitychange) and on the next auth event. Nothing waits forever on a
 * failed read in a way that hurts: greetings are non-essential UI. Then a
 * realtime channel keeps every local store in sync with what OTHER sessions
 * for this user do, and a throttled visibilitychange re-fetch is the backup
 * for a dropped socket. Every merge — hydration, upload-confirmed-unchanged,
 * or realtime — emits `noticeReceiptsBus` so mounted consumers (badges,
 * counts, open modals) can react.
 */
export default function NoticeReceiptsSync({ userId, tenantId }) {
  // KAN-736: Realtime no longer emits DELETE — a deleted receipt arrives as a
  // data-free user-scoped ping and re-runs the same hydrate as resume.
  const rehydrateRef = useRef(null);
  useRowDeletePing({
    userId: userId || null,
    scopes: ['user_notice_receipts'],
    enabled: Boolean(userId && tenantId),
    onDelete: () => { rehydrateRef.current?.(); },
  });
  const lastHydrateAtRef = useRef(0);

  useEffect(() => {
    if (!userId || !tenantId) return undefined;
    setNoticeReceiptsSession({ userId, tenantId });

    let cancelled = false;
    let uploaded = false;
    let hydrating = false;
    let everReady = false;

    const hydrate = async ({ upload } = { upload: false }) => {
      lastHydrateAtRef.current = Date.now();
      // Bounded: PostgREST fetches have no timeout of their own, and an iOS-PWA
      // resume can leave one pending forever — that would pin `hydrating` and
      // silence every retry for the rest of the session. A timeout throws →
      // treated as a failed fetch (not ready; retried on the next event).
      const { data, error } = await withTimeout(fetchNoticeReceipts({ tenantId, userId }), HYDRATE_TIMEOUT_MS);
      if (cancelled) return false;
      if (error) return false; // never READY on a failed read
      mergeFetchedIntoCache(data);
      applyNoticeReceiptsToLocalStores(userId, tenantId, data, undefined);
      if (upload) {
        // One-time migration of this device's existing browser receipts:
        // only what's local-only or newer-than-what-the-server-just-returned
        // (the merge above already pulled local up to the max, so a diff
        // against the PRE-merge server snapshot is exactly that set).
        const serverByKey = new Map((data || []).map((r) => [r.notice_key, r]));
        const localRows = collectAllLocalNoticeReceipts(userId, tenantId, undefined);
        const toUpload = localRows.filter((r) => receiptFieldsDiffer(r, serverByKey.get(r.notice_key)));
        if (toUpload.length > 0) upsertNoticeReceipts({ tenantId, userId, rows: toUpload });
      }
      emitNoticeReceiptsChanged();
      return true;
    };

    // Hydrate once the assurance level is final; safe to call repeatedly.
    // An event that arrives mid-hydration (e.g. MFA verified while the aal1
    // check is in flight) is never dropped: it queues ONE re-run.
    let rerunQueued = false;
    const settleAndHydrate = async () => {
      if (cancelled) return;
      if (hydrating) { rerunQueued = true; return; }
      hydrating = true;
      try {
        if (!(await isAssuranceSettled()) || cancelled) return;
        const ok = await hydrate({ upload: !uploaded });
        if (ok && !cancelled) { uploaded = true; everReady = true; setNoticeReceiptsReady(true); }
      } catch (_e) { /* stays not-ready; retried on the next event */ } finally {
        hydrating = false;
        if (rerunQueued && !everReady && !cancelled) {
          rerunQueued = false;
          setTimeout(() => { void settleAndHydrate(); }, 0);
        } else {
          rerunQueued = false;
        }
      }
    };
    rehydrateRef.current = () => { void settleAndHydrate(); };
    void settleAndHydrate();

    // aal1 → aal2 (MFA verified) arrives as an auth event; defer out of the
    // callback (auth-js deadlock rule) and re-run.
    const authSub = supabase.auth?.onAuthStateChange?.((event) => {
      // Only the not-yet-hydrated case needs this (hourly TOKEN_REFRESHED must not refetch).
      if (event === 'SIGNED_OUT' || everReady) return;
      setTimeout(() => { void settleAndHydrate(); }, 0);
    });

    const channel = supabase
      .channel(`user-notice-receipts-${userId}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'user_notice_receipts', filter: `user_id=eq.${userId}`,
      }, (payload) => {
        const row = payload?.new;
        if (!row || typeof row !== 'object' || !row.notice_key) return;
        // A member of two tenants receives both tenants' rows on this
        // user-filtered channel — apply only this session's tenant.
        if (row.tenant_id !== tenantId) return;
        mergeFetchedIntoCache([row]);
        applyNoticeReceiptsToLocalStores(userId, tenantId, [row], undefined);
        emitNoticeReceiptsChanged();
      })
      .subscribe();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastHydrateAtRef.current < RESUME_MIN_INTERVAL_MS) return;
      void settleAndHydrate();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      rehydrateRef.current = null;
      authSub?.data?.subscription?.unsubscribe?.();
      document.removeEventListener('visibilitychange', onVisibility);
      supabase.removeChannel(channel);
      clearNoticeReceiptsSession();
      clearLastFetchedNoticeReceipts();
      setNoticeReceiptsReady(false);
    };
  }, [userId, tenantId]);

  return null;
}
